"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import { getStudyCategory, getStudyPageTitle } from "@/lib/route-layout";
import { upsertLastStudied } from "@/lib/server-actions/lastStudiedActions";

/** 체류 시간 게이트 — 이만큼 머문 학습 페이지만 "최근 방문"으로 기록(스쳐 지나간 페이지 제외). */
const DWELL_MS = 3000;

/**
 * 학습 페이지 진입 시 카테고리별 "마지막 방문 페이지"를 서버에 기록 (JCG-2166).
 *
 * 홈 "공부 시작하기" 이어학습 모달(ContinueStudyDialog)의 데이터 소스. 로그인 사용자만,
 * 경로 변경 시 1회만 upsert 한다(같은 경로 재호출 방지 ref 가드 → 과도 호출 방지).
 * 카테고리 판정·제목은 route-layout 의 getStudyCategory / getStudyPageTitle 단일 게이트를
 * 재사용한다(이론·코딩·기출·모의고사). 학습 페이지가 아니면 no-op.
 *
 * 체류 시간 게이트: 경로 진입 후 DWELL_MS(3초) 이상 머문 경우에만 기록한다. 그 전에 다른 경로로
 * 이동하면 cleanup 이 타이머를 취소해 기록되지 않으므로, 빠르게 스쳐 간 페이지는 최근 학습으로
 * 남지 않고 write 도 줄어든다(amplification 감소). 실제 기록 시점에 lastRecorded 를 마킹하므로,
 * 일찍 떠난 페이지도 다시 방문해 충분히 머물면 기록될 수 있다.
 *
 * LayoutContent 의 globals 에 마운트돼 셸/클래식 모든 분기에서 상주한다(UI 없음, null 반환).
 */
export default function StudyVisitTracker() {
  const pathname = usePathname();
  const { isLoaded, isSignedIn } = useAuth();
  const lastRecorded = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !pathname) return;
    if (lastRecorded.current === pathname) return;

    const category = getStudyCategory(pathname);
    if (!category) return;

    // 체류 시간 게이트: 충분히 머문 뒤에만 기록. 그 전에 이동하면 아래 cleanup 이 타이머를 취소한다.
    // 기록이 실제로 일어난 시점에 lastRecorded 를 마킹(일찍 떠난 페이지는 재방문 시 다시 시도 가능).
    const timer = setTimeout(() => {
      lastRecorded.current = pathname;
      const title = getStudyPageTitle(pathname);
      void upsertLastStudied(category, pathname, title || null);
    }, DWELL_MS);

    return () => clearTimeout(timer);
  }, [pathname, isLoaded, isSignedIn]);

  return null;
}
